"use client";

import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import {
  ArrowRight,
  Brain,
  Clock,
  Compass,
  Inbox,
  Layers,
  Network,
  RefreshCw,
  Sparkles,
  UserRound,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { apiFetch, apiUrl } from "@/lib/api";
import { formatRelativeTime } from "@/lib/relative-time";
import {
  parseT1Doc,
  SURFACE_LABEL_KEY,
  type Proposition,
} from "@/lib/memory-synthesis";
import MemoryArchivedBanner from "@/components/memory/MemoryArchivedBanner";

interface DocOverview {
  layer: "T2" | "T1";
  key: string;
  exists: boolean;
  updated_at: string | null;
  entry_count: number;
  backlog: number;
}

interface OverviewResponse {
  docs: DocOverview[];
  backups: string[];
}

interface SnapshotResponse {
  surface: string;
  entities: unknown[];
}

const SURFACES = [
  "chat",
  "notebook",
  "quiz",
  "kb",
  "book",
  "partner",
  "cowriter",
] as const;

const T1_VISIBLE = ["recent", "profile", "scope"] as const;

const PROP_ICON: Record<string, LucideIcon> = {
  profile: UserRound,
  recent: Clock,
  scope: Compass,
};

// T1 slot key → i18n label key (English source; ko value in app.json).
const T1_LABEL_KEY: Record<string, string> = {
  profile: "Profile",
  recent: "Recent timeline",
  scope: "Knowledge scope",
};

export default function MemoryHub() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "en";

  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [t3Total, setT3Total] = useState<number | null>(null);
  const [props, setProps] = useState<Proposition[]>([]);
  const [loading, setLoading] = useState(true);

  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [accepting, setAccepting] = useState<Set<string>>(new Set());
  const [errored, setErrored] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, snaps, t1docs] = await Promise.all([
        apiFetch(apiUrl("/api/v1/memory/overview")).then(
          (r) => r.json() as Promise<OverviewResponse>,
        ),
        Promise.all(
          SURFACES.map((s) =>
            apiFetch(apiUrl(`/api/v1/memory/snapshot/${s}`))
              .then((r) => r.json())
              .then((d: SnapshotResponse) => d?.entities?.length ?? 0)
              .catch(() => 0),
          ),
        ),
        Promise.all(
          T1_VISIBLE.map((k) =>
            apiFetch(apiUrl(`/api/v1/memory/doc/T1/${k}`))
              .then((r) => r.json())
              .then((d: { content?: string }) => parseT1Doc(k, d?.content ?? ""))
              .catch(() => [] as Proposition[]),
          ),
        ),
      ]);
      setOverview(ov);
      setT3Total(snaps.reduce((acc, n) => acc + n, 0));
      setProps(t1docs.flat());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const t2Docs = (overview?.docs || []).filter((d) => d.layer === "T2");
  const t1Docs = (overview?.docs || []).filter(
    (d) => d.layer === "T1" && d.key !== "preferences",
  );
  const t2Total = t2Docs.reduce((acc, d) => acc + d.entry_count, 0);
  const t1Total = t1Docs.reduce((acc, d) => acc + d.entry_count, 0);
  const latestBackup = overview?.backups?.[overview.backups.length - 1] ?? null;

  const distinctSurfaces = new Set(props.flatMap((p) => p.surfaces)).size;
  const t1UpdatedAt = t1Docs
    .map((d) => d.updated_at)
    .filter((x): x is string => Boolean(x))
    .sort()
    .pop();

  const backlogDocs = (overview?.docs || []).filter((d) => d.backlog > 0);
  const visibleBacklog = backlogDocs.filter(
    (d) => !dismissed.has(`backlog:${d.layer}:${d.key}`),
  );
  const tidyEmpty = !loading && visibleBacklog.length === 0;

  const acceptBacklog = useCallback(
    async (d: DocOverview) => {
      const id = `backlog:${d.layer}:${d.key}`;
      setErrored((p) => remove(p, id));
      setAccepting((p) => add(p, id));
      try {
        const res = await apiFetch(
          apiUrl(`/api/v1/memory/doc/${d.layer}/${d.key}/update`),
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ language: locale }),
          },
        );
        if (!res.ok) throw new Error(String(res.status));
        await res.text(); // drain the SSE run to completion
        setDismissed((p) => add(p, id));
        await load();
      } catch {
        setErrored((p) => add(p, id));
      } finally {
        setAccepting((p) => remove(p, id));
      }
    },
    [load, locale],
  );

  const dismiss = useCallback((id: string) => {
    setDismissed((p) => add(p, id));
  }, []);

  const dismissAll = useCallback(() => {
    setDismissed((p) => {
      const n = new Set(p);
      for (const d of visibleBacklog) n.add(`backlog:${d.layer}:${d.key}`);
      return n;
    });
  }, [visibleBacklog]);

  const docLabel = (d: DocOverview) =>
    d.layer === "T1"
      ? t(T1_LABEL_KEY[d.key] ?? d.key)
      : t(SURFACE_LABEL_KEY[d.key] ?? d.key);

  return (
    <div className="space-y-10">
      <header className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--primary)]/10 text-[var(--primary)]">
            <Brain className="h-5 w-5" />
          </span>
          <h1 className="font-serif text-[28px] font-semibold tracking-tight text-[var(--foreground)] md:text-[32px]">
            {t("Memory")}
          </h1>
        </div>
        <p className="max-w-2xl text-[14px] text-[var(--muted-foreground)] md:text-[15px]">
          {t(
            "Everything AI TokTok remembers about you, organised across three layers. Click into any layer to inspect or curate it.",
          )}
        </p>
        <div className="flex items-center gap-3 text-[13.5px] text-[var(--muted-foreground)]">
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--background)] px-2.5 py-1 transition hover:bg-[var(--muted)]"
          >
            <RefreshCw className={loading ? "h-3 w-3 animate-spin" : "h-3 w-3"} />
            {t("Re-tidy")}
          </button>
          <Link
            href="/settings/memory"
            className="inline-flex items-center gap-1.5 rounded-md border border-transparent px-2.5 py-1 transition hover:bg-[var(--muted)]"
          >
            {t("Memory settings")}
          </Link>
        </div>
      </header>

      <SynthHero
        props={props}
        loading={loading}
        understandings={props.length}
        surfaces={distinctSurfaces}
        freshness={
          t1UpdatedAt
            ? formatRelativeTime(Date.parse(t1UpdatedAt) / 1000, locale)
            : null
        }
      />

      <section className="space-y-4">
        <SectionHead
          title={t("Cleanup suggestions")}
          hint={t(
            "New records not yet folded into memory. Fold them in now, or dismiss and it won't ask again.",
          )}
          action={
            !tidyEmpty ? (
              <button
                type="button"
                onClick={dismissAll}
                className="text-[13.5px] text-[var(--muted-foreground)] transition hover:text-[var(--foreground)]"
              >
                {t("Dismiss all")}
              </button>
            ) : null
          }
        />
        {tidyEmpty ? (
          <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--card)] px-6 py-10 text-center text-[13.5px] text-[var(--muted-foreground)]">
            {t("Nothing to tidy right now 👍")}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {visibleBacklog.map((d) => {
              const id = `backlog:${d.layer}:${d.key}`;
              return (
                <TidyCard
                  key={id}
                  icon={Inbox}
                  tone="primary"
                  kind={t("Not yet folded")}
                  title={t("{{label}} · {{n}} new records", {
                    label: docLabel(d),
                    n: d.backlog,
                  })}
                  source={docLabel(d)}
                  busy={accepting.has(id)}
                  failed={errored.has(id)}
                  acceptLabel={t("Fold now")}
                  busyLabel={t("Folding…")}
                  failLabel={t("Failed — retry")}
                  laterLabel={t("Later")}
                  onAccept={() => void acceptBacklog(d)}
                  onDismiss={() => dismiss(id)}
                />
              );
            })}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <SectionHead
          title={t("Explore memory in detail")}
          hint={t("Three layers, from raw traces to cross-surface synthesis.")}
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <LayerCard
            href="/memory/t1"
            icon={Network}
            title={t("T1 · Cross-surface knowledge")}
            tag={t("Synthesis")}
            stat={t1Total.toLocaleString()}
            statLabel={t("propositions across {{n}} slots", {
              n: t1Docs.length || T1_VISIBLE.length,
            })}
            detail={t(
              "Cross-surface synthesis — profile, recent timeline and knowledge scope, with hedged claims backed by T2 evidence.",
            )}
          />
          <LayerCard
            href="/memory/t2"
            icon={Workflow}
            title={t("T2 · Per-surface summaries")}
            tag={t("Curated")}
            stat={t2Total.toLocaleString()}
            statLabel={t("facts across {{n}} surfaces", {
              n: t2Docs.length || SURFACES.length,
            })}
            detail={t(
              "Surface-specific facts extracted by the consolidator. Run Update / Audit / Dedup per doc.",
            )}
          />
          <LayerCard
            href="/memory/t3"
            icon={Layers}
            title={t("T3 · Workspace mirror")}
            tag={t("Live")}
            stat={t3Total === null ? "…" : t3Total.toLocaleString()}
            statLabel={t("entities tracked")}
            detail={t(
              "Snapshot of your live workspace across {{n}} surfaces. Refresh to record changes.",
              { n: SURFACES.length },
            )}
          />
        </div>

        <GraphCallout />

        <MemoryArchivedBanner latestBackup={latestBackup} variant="compact" />
      </section>
    </div>
  );
}

function add(set: Set<string>, id: string): Set<string> {
  const n = new Set(set);
  n.add(id);
  return n;
}

function remove(set: Set<string>, id: string): Set<string> {
  const n = new Set(set);
  n.delete(id);
  return n;
}

function SectionHead({
  title,
  hint,
  action,
}: {
  title: string;
  hint: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div className="space-y-1">
        <h2 className="text-[17px] font-semibold text-[var(--foreground)]">
          {title}
        </h2>
        <p className="max-w-2xl text-[13.5px] text-[var(--muted-foreground)]">
          {hint}
        </p>
      </div>
      {action}
    </div>
  );
}

function SynthHero({
  props,
  loading,
  understandings,
  surfaces,
  freshness,
}: {
  props: Proposition[];
  loading: boolean;
  understandings: number;
  surfaces: number;
  freshness: string | null;
}) {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden rounded-3xl border border-[var(--border)] bg-[var(--card)] p-7 md:p-8">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 90% at 88% 0%, color-mix(in srgb, var(--primary) 15%, transparent), transparent 65%)",
        }}
      />
      <div className="relative space-y-5">
        <div className="space-y-2.5">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--primary)]/30 bg-[var(--primary)]/10 px-2.5 py-1 text-[13px] font-medium text-[var(--primary)]">
            <Sparkles className="h-3.5 w-3.5" />
            {t("Synthesis")}
          </span>
          <h2 className="font-serif text-[22px] font-semibold tracking-tight text-[var(--foreground)] md:text-[26px]">
            {t("What AI TokTok remembers about you")}
          </h2>
          <p className="max-w-2xl text-[13.5px] leading-relaxed text-[var(--muted-foreground)]">
            {t(
              "Everything it saw across chat, notes, quizzes and courses, woven into one understanding.",
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <StatPill text={t("{{n}} understandings", { n: understandings })} />
            <StatPill text={t("from {{n}} surfaces", { n: surfaces })} />
            {freshness ? (
              <StatPill text={t("synced {{time}}", { time: freshness })} />
            ) : null}
          </div>
        </div>

        {!loading && understandings === 0 ? (
          <p className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--background)]/50 px-5 py-8 text-center text-[13.5px] text-[var(--muted-foreground)]">
            {t("Not enough records yet to weave a cross-surface picture.")}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {props.map((p, i) => {
              const Icon = PROP_ICON[p.sourceKey] ?? Sparkles;
              return (
                <div
                  key={i}
                  className="flex gap-3 rounded-2xl border border-[var(--border)] bg-[var(--background)]/60 p-4"
                >
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[var(--primary)]/10 text-[var(--primary)]">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 space-y-2">
                    <p className="text-[13.5px] leading-relaxed text-[var(--foreground)]">
                      {p.text}
                    </p>
                    {p.surfaces.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {p.surfaces.map((s) => (
                          <span
                            key={s}
                            className="rounded-full border border-[var(--border)] bg-[var(--card)] px-2 py-0.5 text-[13px] text-[var(--muted-foreground)]"
                          >
                            {t(SURFACE_LABEL_KEY[s] ?? s)}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function StatPill({ text }: { text: string }) {
  return (
    <span className="rounded-full border border-[var(--border)] bg-[var(--background)]/70 px-2.5 py-1 text-[13.5px] font-medium text-[var(--muted-foreground)]">
      {text}
    </span>
  );
}

interface TidyCardProps {
  icon: LucideIcon;
  tone: "primary" | "amber";
  kind: string;
  title: string;
  source: string;
  acceptLabel: string;
  laterLabel: string;
  busy?: boolean;
  failed?: boolean;
  busyLabel?: string;
  failLabel?: string;
  onAccept?: () => void;
  onDismiss: () => void;
}

function TidyCard({
  icon: Icon,
  tone,
  kind,
  title,
  source,
  acceptLabel,
  laterLabel,
  busy,
  failed,
  busyLabel,
  failLabel,
  onAccept,
  onDismiss,
}: TidyCardProps) {
  const toneClass =
    tone === "amber"
      ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
      : "bg-[var(--primary)]/10 text-[var(--primary)]";
  const acceptText = busy ? busyLabel : failed ? failLabel : acceptLabel;
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[var(--border)] bg-[var(--card)] p-5">
      <div className="flex items-start gap-3">
        <span
          className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${toneClass}`}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <span className="text-[13px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
            {kind}
          </span>
          <h3 className="text-[14px] font-semibold leading-snug text-[var(--foreground)]">
            {title}
          </h3>
          <span className="inline-block rounded-full border border-[var(--border)] bg-[var(--background)] px-2 py-0.5 text-[13px] text-[var(--muted-foreground)]">
            {source}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onAccept}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-lg bg-[var(--primary)] px-3 py-1.5 text-[13.5px] font-medium text-[var(--primary-foreground)] transition hover:opacity-90 disabled:opacity-60"
        >
          {busy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : null}
          {acceptText}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-[13.5px] text-[var(--muted-foreground)] transition hover:bg-[var(--muted)]"
        >
          {laterLabel}
        </button>
      </div>
    </div>
  );
}

function GraphCallout() {
  const { t } = useTranslation();
  return (
    <Link
      href="/memory/graph"
      className="group relative block overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--card)] p-6 transition hover:-translate-y-[1px] hover:border-[var(--primary)]/40 hover:shadow-sm"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-80"
        style={{
          background:
            "radial-gradient(ellipse 60% 80% at 92% 50%, color-mix(in srgb, var(--primary) 16%, transparent), transparent 70%)",
        }}
      />
      <div className="relative flex items-center gap-5">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[var(--primary)]/10 text-[var(--primary)]">
          <Sparkles className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-[15px] font-semibold text-[var(--foreground)]">
              {t("Memory graph")}
            </h3>
            <span className="rounded-full border border-[var(--border)] bg-[var(--background)]/60 px-2 py-0.5 text-[13px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
              {t("New")}
            </span>
          </div>
          <p className="mt-1 text-[13.5px] leading-relaxed text-[var(--muted-foreground)]">
            {t(
              "See all three layers at once — T1 synthesis at the centre, T2 facts in the middle, T3 traces on the outside. Hover any node for a preview.",
            )}
          </p>
        </div>
        <ArrowRight className="hidden h-4 w-4 shrink-0 text-[var(--primary)] transition group-hover:translate-x-0.5 md:block" />
      </div>
    </Link>
  );
}

interface LayerCardProps {
  href: string;
  icon: LucideIcon;
  title: string;
  tag: string;
  stat: string;
  statLabel: string;
  detail: string;
}

function LayerCard({
  href,
  icon: Icon,
  title,
  tag,
  stat,
  statLabel,
  detail,
}: LayerCardProps) {
  return (
    <Link
      href={href}
      className="group flex flex-col gap-4 rounded-2xl border border-[var(--border)] bg-[var(--card)] p-6 transition hover:-translate-y-[1px] hover:border-[var(--primary)]/40 hover:shadow-sm"
    >
      <div className="flex items-start justify-between">
        <span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--primary)]/10 text-[var(--primary)]">
          <Icon className="h-4 w-4" />
        </span>
        <span className="rounded-full border border-[var(--border)] bg-[var(--background)] px-2 py-0.5 text-[13px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
          {tag}
        </span>
      </div>
      <div className="space-y-1">
        <h2 className="text-[15px] font-semibold text-[var(--foreground)]">
          {title}
        </h2>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-[28px] font-semibold tracking-tight text-[var(--foreground)]">
          {stat}
        </span>
        <span className="text-[13.5px] text-[var(--muted-foreground)]">
          {statLabel}
        </span>
      </div>
      <p className="text-[13.5px] leading-relaxed text-[var(--muted-foreground)]">
        {detail}
      </p>
      <div className="mt-auto inline-flex items-center gap-1 text-[13.5px] font-medium text-[var(--primary)] opacity-0 transition group-hover:opacity-100">
        <span>{tag}</span>
        <ArrowRight className="h-3.5 w-3.5" />
      </div>
    </Link>
  );
}
